<template>
  <van-field
    v-bind="props.fieldProps"
    v-model="code"
    class="from-input verify-code"
    type="tel"
    :name="name"
    :label="label"
    :placeholder="placeholder"
    :maxlength="maxlength"
    :rules="[{ required, message: message || placeholder }]"
    :formatter="removeSpaces"
  >
    <template #right-icon v-if="!!leftIcon">
      <svg-icon :icon-name="leftIcon" :width="46" current-color />
    </template>
    <template #button>
      <div class="code-img" @click="getImageVerifyCode" v-if="name === 'verifycode' && verifyCodeData?.image">
        <img v-lazy="verifyCodeData?.image" />
      </div>
      <div class="email-code" v-if="name === 'emailCode' || name === 'smsCode'">
        <span
          class="action-btn"
          :class="{ loading: reqCodeLoading }"
          @click="getTextVerifyCode"
          v-show="!isShowCountDown"
        >
          获取验证码
        </span>
        <van-count-down
          ref="countDown"
          :time="1000 * 60"
          :auto-start="false"
          format="ss s"
          @finish="isShowCountDown = false"
          v-show="isShowCountDown"
        />
      </div>
    </template>
  </van-field>
</template>

<script setup lang="ts">
import { computed, defineModel, onMounted, ref } from 'vue';
import { mb_t, useI18n } from '@star/languages';
import { useUserStore } from '@star/stores';
import { emailRegisterFrom, removeSpaces } from '@star/utils';
import type { CountDownInstance, FieldProps } from 'vant';
import { showFailToast, showNotify, showSuccessToast } from 'vant';
import { useNEGuardian } from '@star/hooks';

// v-if="col.dataIndex === 'verifycode' || col.dataIndex === 'emailCode' || col.dataIndex === 'smsCode'"
type Props = {
  label?: string;
  placeholder?: string;
  name?: string;
  message?: string;
  maxlength?: number | string;
  leftIcon?: string;
  required?: boolean;
  account?: string; // 接收验证信息的邮箱，手机号码
  loginType?: string; // phone_captcha, email_captcha
  country?: string; // 手机区号
  type: 1 | 2; // 1注册 2登录
  fieldProps?: FieldProps;
};

const props = withDefaults(defineProps<Props>(), {
  maxlength: 8,
  required: true,
  account: '',
});

const { fetchVerifyCode, reqEmailCode, reqSMSCode, reqSpecialStatus } = useUserStore();
const { getNegProductID, initNeg, getNegToken, token: negToken } = useNEGuardian(); // 网易风控hooks

const { t } = useI18n();

const code = defineModel();

const countDown = ref<CountDownInstance>();
const verifyCodeData = ref<any>(); // 图片验证返回的相关数据
const reqCodeLoading = ref<boolean>(false);
const isShowCountDown = ref<boolean>(false);

const name = computed(() => {
  return props.name || 'code';
});

const label = computed(() => {
  return props.label ?? '验证码';
});

const placeholder = computed(() => {
  return props.placeholder ?? '请输入验证码';
});

const isEmailCode = computed(() => {
  return props.loginType === 'email_captcha';
});

const isPhone = computed(() => {
  return props.loginType === 'phone_captcha' || props.loginType === 'phone_pwd_captcha';
});

const getImageVerifyCode = async () => {
  verifyCodeData.value = await fetchVerifyCode({ type: props.type });
};

const getTextVerifyCode = async () => {
  console.log(props.account, isEmailCode.value, isPhone.value, 'props.account');
  if (reqCodeLoading.value) return;
  if (!props.account) {
    showNotify({ type: 'warning', message: mb_t(isPhone.value ? '请输入手机号码！' : '请输入邮箱账号') });
    return;
  }
  try {
    reqCodeLoading.value = true;
    if (isEmailCode.value) {
      await getEmailCode();
    } else if (isPhone.value) {
      const negProductId = await getNegProductID();
      await initNeg(negProductId);
      await getNegToken();
      await getPhoneCode();
    }
  } finally {
    reqCodeLoading.value = false;
  }
};

const getEmailCode = async () => {
  if (!emailRegisterFrom(props.account)) return;
  try {
    const res = await reqEmailCode({
      email_to_account: props.account,
      verifycode: '',
      email_type: props.type === 1 ? 3 : 2, // 2 注册 3 登录
    });
    if (res?.response_code === '9999') {
      showSuccessToast(mb_t('发送成功'));
      countDownReset();
    } else {
      // showFailToast(mb_t(res));
    }
  } catch (err: any) {
    if (err?.info) {
      showFailToast(mb_t(err?.info));
    }
  }
};

const getPhoneCode = async () => {
  // if (!checkPhone(props.account)) return;
  try {
    const res = await reqSMSCode({
      token: negToken.value, // 网易风控token
      receive_mobile: props.account,
      country: props.country,
      verifycode: '',
      type: props.type === 1 ? 2 : 1, // 1注册短信，2登录短信，3忘记密码短信
    });
    if (res?.response_code === '9999') {
      showSuccessToast(mb_t('发送成功'));
      countDownReset();
    } else {
      res?.info && showFailToast(mb_t(res.info));
    }
  } catch (err: any) {
    if (err?.info) {
      showFailToast(mb_t(err?.info));
    }
  }
};

const countDownReset = () => {
  console.log(countDown.value);
  isShowCountDown.value = true;
  countDown.value?.reset?.();
  countDown.value?.start?.();
};

const init = async () => {
  verifyCodeData.value = undefined;
  if (name.value === 'verifycode') {
    await getImageVerifyCode();
  }
};

onMounted(async () => {
  await init();
});
</script>

<style lang="less" scoped>
.from-input.verify-code {
  .code-img {
    height: 65px;

    img {
      height: 100%;
    }
  }

  .action-btn {
    color: var(--text-primary-color);
    font-size: 42px;
    font-weight: 500;

    &.loading {
      color: #bcbcbc;
    }
  }
}
</style>
